import React, { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import "../TicketManagment/style.css";
import SearchInput from "../../../components/common/SearchInputField";
import GroupSelectDropdown from "../../../components/dashboardcomponents/ProfileComponents/GroupSelectDropdown";
import UserTrafficTable from "../../../components/dashboardcomponents/Home/UserTraficTable";
import { userData } from "../../../constants/index";
import UserDetail from "../../../components/dashboardcomponents/Traffic/UserDetail";
import { selectIsTrafficUserDetailSidebarOpen, openTrafficUserDetailSidebar, closeTrafficUserDetailSidebar } from "../../../store/features/trafficUserDetailSidebarSlice";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { isHeaderInlinePageTitleEnabled } from "../../../config/featureFlags";
import { useAnalyticsGroupId } from "../../../hooks/useAnalyticsGroupId";

function TrafficManagement() {
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const { selectedGroupId, setSelectedGroupId } = useAnalyticsGroupId();
  const [selectedUser, setSelectedUser] = useState(null);
  const dispatch = useDispatch();
  const currentLanguage = useSelector(
    (state) => state.language.currentLanguage
  );
  const t = useSelector(
    (state) => state.language.translations[currentLanguage]
  );

  const isUserDetailSidebarOpen = useSelector(selectIsTrafficUserDetailSidebarOpen);

  const isTabActive = (tabIndex) => activeTab === tabIndex;

  const trafficData = useSelector((s) => s.userTraffic?.data || []);
  const pagination = useSelector((s) => s.userTraffic?.pagination);

  const counts = useMemo(() => {
    const source = Array.isArray(trafficData) && trafficData.length > 0 ? trafficData :[];
    const getCount = (status) => source.filter((u) => ((u.status || u.trafficStatus || 'browsing') === status)).length;
    const browsing = getCount("browsing");
    const queued = getCount("queued");
    const chatting = getCount("chatting");
    const invited = getCount("invited");
    const total = pagination?.total ?? source.length;
    return { total, browsing, queued, chatting, invited };
  }, [trafficData, pagination]);

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pathname = location.pathname;
  const isTrafficPage = pathname === "/user-traffic";
  
  // Sync open state and selected user with URL param userId
  useEffect(() => {
    const userIdParam = searchParams.get("userId");
    if (userIdParam) {
      const source = Array.isArray(trafficData) && trafficData.length > 0 ? trafficData : userData;
      const user = source.find((u) => String(u.id || u._id) === String(userIdParam));
      if (user) {
        setSelectedUser(user);
        dispatch(openTrafficUserDetailSidebar());
      }
    } else {
      dispatch(closeTrafficUserDetailSidebar());
      setSelectedUser(null);
    }
  }, [searchParams, dispatch, trafficData]);

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    dispatch(openTrafficUserDetailSidebar());
    const params = new URLSearchParams(searchParams.toString());
    params.set("userId", user.id || user._id);
    setSearchParams(params, { replace: false });
  };

  const headerAndTabs = useMemo(
    () => (
      <div className="TicketManagment-Container001">
        {/* Legacy in-page title + group — hidden when header shows them (Application V2) */}
        {!isHeaderInlinePageTitleEnabled && (
          <div className="profile-header-row">
            <p className="Ticket-text">Traffic</p>
            <GroupSelectDropdown
              onSelect={setSelectedGroupId}
              value={selectedGroupId}
            />
          </div>
        )}
        <div
          className="Ticket-main-header"
          style={{
            marginTop: isHeaderInlinePageTitleEnabled ? 0 : undefined,
          }}
        >
          <div className="Ticket-text-section">
            <p
              className={`ticket-header-text ${
                isTabActive(0) ? "TicketHeaderActive" : ""
              }`}
              onClick={() => setActiveTab(0)}
            >
              {`All Visitors (${counts.total})`}
            </p>
            <p
              className={`ticket-header-text ${
                isTabActive(1) ? "TicketHeaderActive" : ""
              }`}
              onClick={() => setActiveTab(1)}
            >
              {`Browsing (${counts.browsing})`}
            </p>
            <p
              className={`ticket-header-text ${
                isTabActive(2) ? "TicketHeaderActive" : ""
              }`}
              onClick={() => setActiveTab(2)}
            >
              {`Queued (${counts.queued})`}
            </p>
            <p
              className={`ticket-header-text ${
                isTabActive(3) ? "TicketHeaderActive" : ""
              }`}
              onClick={() => setActiveTab(3)}
            >
              {`Chatting (${counts.chatting})`}
            </p>

            <p
              className={`ticket-header-text ${
                isTabActive(5) ? "TicketHeaderActive" : ""
              }`}
              onClick={() => setActiveTab(5)}
            >
              {`Invited (${counts.invited})`}
            </p>
          </div>
          <div
            className="inviteButton-container"
            style={{
              marginTop: 16,
              ...(isHeaderInlinePageTitleEnabled ? { marginBottom: 16 } : {}),
            }}
          >
            <SearchInput
              noTitle
              placeholder="Search"
              className="SearchInput-teamsection SearchTraffic"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>
    ),
    [activeTab, searchQuery, counts, selectedGroupId, setSelectedGroupId, isHeaderInlinePageTitleEnabled]
  );

  return (
    <div
      className="TicketManagment-main-container"
      style={{
        position: "relative",
        ...(isHeaderInlinePageTitleEnabled ? { marginTop: 0 } : {}),
      }}
    >
      {headerAndTabs}
      <div className="Team-Main-Container" style={{ padding: "0 24px" }}>
        <UserTrafficTable
          activeTab={activeTab}
          searchQuery={searchQuery}
          onUserSelect={handleUserSelect}
          counts={counts}
          selectedGroupId={selectedGroupId}
        />
      </div>
      {isUserDetailSidebarOpen && isTrafficPage && (
        <UserDetail selectedUser={selectedUser} />
      )}
    </div>
  );
}

export default TrafficManagement;
